import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import {
  ShipmentFiltersDisplayParams,
  ShipmentFiltersFieldsParams,
} from '@features/shipments/services/shipment-filters.service';
import { ConfigConstant } from '@shared/constants';
import { FilterService, StatusFilterOption } from '@features/filter/services/filter.service';
import { ShipmentStatus, TrackingType } from '@shared-features/enums/shipments.enum';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-filtering-chips',
  templateUrl: './filtering-chips.component.html',
  styleUrls: ['./filtering-chips.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FilteringChipsComponent implements OnDestroy {
  @Input() set displays(items: ShipmentFiltersDisplayParams) {
    const textMap: ShipmentFiltersDisplayParams = {};
    const extraMap: Record<string, string> = {};
    const props = Object.keys(items || {}) as (keyof ShipmentFiltersDisplayParams)[];
    props.forEach((prop) => {
      const value = items[prop];
      if (Array.isArray(value)) {
        textMap[prop] = value[0];
        extraMap[prop] = this.getListExtraLength(value);
      } else {
        textMap[prop] = value;
      }
    });
    this.displaysTextMap = textMap;
    this.displaysExtraMap = extraMap;
    this.displaysDetails = items;
    this.changeDetectorRef.markForCheck();
  }
  @Input() params: ShipmentFiltersFieldsParams;
  @Output() updateParams = new EventEmitter<[Partial<ShipmentFiltersFieldsParams>, Partial<ShipmentFiltersDisplayParams>, boolean]>();

  protected displaysTextMap: ShipmentFiltersDisplayParams = {};
  protected displaysExtraMap: Record<string, string> = {};
  protected displaysDetails: ShipmentFiltersDisplayParams = {};

  protected readonly resetTrackingType = TrackingType.All;
  protected readonly resetStatus = ShipmentStatus.None;

  private statusOptions: StatusFilterOption[] = [];
  private readonly destroy$ = new Subject<void>();

  private readonly directionLabels: Record<string, string> = { INBOUND: 'shipments.Inbound', OUTBOUND: 'shipments.Outbound' };
  private readonly customerServiceTypeLabels: Record<string, string> = { SERVICE: 'shipments.Ops', SAAS: 'shipments.Saas' };
  private readonly tripTypeLabels: Record<string, string> = { SINGLE: 'shipments.Requests.Single', MULTIPLE: 'shipments.Requests.Multiple' };
  private readonly trackingLabels: Record<string, string> = { AVL: 'shipments.AVL', MOBILE: 'shipments.Mobile', EMPTY: 'shipments.NoTracking' };

  constructor(private changeDetectorRef: ChangeDetectorRef, private filterService: FilterService) {
    this.filterService.shipmentStatusOptions$.pipe(takeUntil(this.destroy$)).subscribe((options) => {
      this.statusOptions = options || [];
      this.changeDetectorRef.markForCheck();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  protected directionLabel(value: string): string {
    return this.directionLabels[value] ?? null;
  }

  protected customerServiceTypeLabel(value: string): string {
    return this.customerServiceTypeLabels[value] ?? null;
  }

  protected tripTypeLabel(value: string): string {
    return this.tripTypeLabels[value] ?? null;
  }

  protected trackingLabel(value: string): string {
    return this.trackingLabels[value] ?? null;
  }

  protected bayanLabel(value: boolean): string {
    if (value === true) {
      return 'shipments.BayanVerified';
    }
    if (value === false) {
      return 'shipments.BayanNotVerified';
    }
    return null;
  }

  protected statusLabel(value: string): string {
    if (!value || value === this.resetStatus) {
      return null;
    }
    return this.statusOptions.find((option) => option.value === value)?.label ?? null;
  }

  // True when at least one chip is shown, so the applied-filters bar can be hidden while empty.
  protected get hasActiveChips(): boolean {
    const d = this.displaysTextMap;
    const p = this.params;
    return (
      this.hasEntityChip(d.customerName) ||
      this.hasEntityChip(d.receiverName) ||
      this.hasEntityChip(d.fleetName) ||
      this.hasIdChip(p?.truckId, d.truckName) ||
      this.hasIdChip(p?.driverId, d.driverName) ||
      this.hasEntityChip(d.creatorName) ||
      this.hasEntityChip(d.fromCityName) ||
      this.hasEntityChip(d.toCityName) ||
      !!this.statusLabel(p?.status) ||
      !!this.customerServiceTypeLabel(p?.customerServiceType) ||
      !!this.bayanLabel(p?.isBayanVerified) ||
      !!this.tripTypeLabel(p?.shipmentRequestFilterCount) ||
      !!this.trackingLabel(p?.trackingTypeFilter) ||
      !!this.directionLabel(p?.direction)
    );
  }

  protected hasEntityChip(value: string): boolean {
    return !!value?.length && !value.includes(ConfigConstant.ALL);
  }

  // Truck/driver arrive as a navigation context, never as one of the drawer's "All X" lookups,
  // so they key off the id: hasEntityChip's substring test would hide names like "Allison".
  protected hasIdChip(id: number, name: string): boolean {
    return !!id && !!name?.length;
  }

  // Reset every filter this bar can show, in one emission.
  protected clearAll(): void {
    const fields: Partial<ShipmentFiltersFieldsParams> & { offset: number } = {
      customerId: null,
      receiverId: null,
      fleetId: null,
      truckId: null,
      driverId: null,
      creator: null,
      fromCityId: null,
      toCityId: null,
      status: this.resetStatus,
      customerServiceType: null,
      isBayanVerified: null,
      shipmentRequestFilterCount: null,
      trackingTypeFilter: this.resetTrackingType,
      direction: null,
      offset: 0,
    };
    const displays: Partial<ShipmentFiltersDisplayParams> = {
      customerName: null,
      receiverName: null,
      fleetName: null,
      truckName: null,
      driverName: null,
      creatorName: null,
      fromCityName: null,
      toCityName: null,
    };
    this.updateParams.emit([fields, displays, true]);
  }

  private getListExtraLength(list: string[]): string {
    return list.length > 1 ? `+${list.length - 1}` : '';
  }
}
